import {
  CreateDataRoomShareableLinkForm,
  DATA_ROOM_NDA_APPLY_TO,
  DATA_ROOM_SHAREABLE_LINK_ACCESS,
  DATA_ROOM_SHAREABLE_LINK_DESTINATION,
  DATA_ROOM_SHAREABLE_LINK_EXPIRES,
  DataRoomShareableLink,
} from 'models/ShareableLink';
import { DataRoomNda } from 'router/subrouters/DataRoom/models/DataRoomNDA';
import {
  getIsLinkExpiresOther,
  getIsAccessAddPasword,
  getIsAccessAnyone,
  getIsAccessAddSpecificRecipiens,
} from 'router/subrouters/DataRoom/utils';
import { getArrayOfEmails } from 'utils';
import validations from 'validations';
import { DropzoneFile } from 'components/Dropzone/utils';
import { FILE_TYPES } from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/data/DataTypes';

export const createWatermarkParamsForPreview = ({
  watermarkDiagonal,
  watermarkIpAddress,
  watermarkHeaderAndFooter,
  watermarkNameAndEmail,
}: {
  watermarkDiagonal: boolean;
  watermarkIpAddress: boolean;
  watermarkHeaderAndFooter: boolean;
  watermarkNameAndEmail: boolean;
}) => ({
  watermarkDiagonal,
  watermarkIpAddress,
  watermarkHeader: watermarkHeaderAndFooter,
  watermarkFooter: watermarkHeaderAndFooter,
  watermarkFullName: watermarkNameAndEmail,
  watermarkEmail: watermarkNameAndEmail,
});

export const createInitialValues = () => ({
  linkAccess: DATA_ROOM_SHAREABLE_LINK_ACCESS.ANYONE_WITH_LINK,
  linkExpires: DATA_ROOM_SHAREABLE_LINK_EXPIRES.SEVEN_DAYS,
  linkDestination: DATA_ROOM_SHAREABLE_LINK_DESTINATION.DIRECT_ACCESS,
  allowDownloads: true,
  includeNdaProtection: false,
  includeTermsAndConditions: false,
  useExistingNda: false,
  ndaApplyTo: DATA_ROOM_NDA_APPLY_TO.ONLY_UNSIGNED,
  recipientEmailAddresses: [] as string[],
  notifyOnSecureLinkAccess: true,
  notifyOnWaitingRoomEntry: true,
});

export const createInitialValuesFromLink = (
  link: DataRoomShareableLink,
  dataRoomNdas: DataRoomNda[],
): CreateDataRoomShareableLinkForm => {
  const { dataRoomNdaId, dataRoomNda, linkDestination, ...rest } = link;

  const useExistingNda = !!dataRoomNdas?.find(
    ({ _id }) => _id === dataRoomNdaId,
  );

  const nDataRoomNda = dataRoomNda?.file
    ? { ...dataRoomNda.file, _id: dataRoomNda._id }
    : null;

  return {
    ...rest,
    useExistingNda,
    ndaApplyTo: link.ndaApplyTo || DATA_ROOM_NDA_APPLY_TO.ONLY_UNSIGNED,
    linkDestination:
      linkDestination || DATA_ROOM_SHAREABLE_LINK_DESTINATION.DIRECT_ACCESS,
    ...(useExistingNda ? { dataRoomNdaId } : { dataRoomNda: nDataRoomNda }),
  };
};

export const getPayloadFromFormValues = ({
  name,
  linkAccess,
  password,
  recipientEmailAddresses,
  optionalNote,
  anyoneWithNonPublicDomain,
  emailDomain,
  linkExpires,
  linkExpiresDate,
  allowDownloads,
  includeNdaProtection,
  useExistingNda,
  dataRoomNdaId,
  dataRoomNda,
  ndaApplyTo,
  linkDestination,
  includeTermsAndConditionProtection,
  termsAndConditionsContent,
  notifyOnSecureLinkAccess,
  notifyOnWaitingRoomEntry,
}: CreateDataRoomShareableLinkForm) => ({
  name,
  linkAccess,
  linkExpires,
  allowDownloads,
  includeNdaProtection,
  includeTermsAndConditionProtection,
  notifyOnSecureLinkAccess,
  notifyOnWaitingRoomEntry,
  ...(includeTermsAndConditionProtection && { termsAndConditionsContent }),
  ...(includeNdaProtection && useExistingNda && { dataRoomNdaId }),
  ...(includeNdaProtection &&
    !useExistingNda && { dataRoomNdaId: dataRoomNda?._id }),
  ...(includeNdaProtection && { ndaApplyTo }),
  ...(getIsLinkExpiresOther(linkExpires) && { linkExpiresDate }),
  ...(getIsAccessAddPasword(linkAccess) && { password }),
  ...(getIsAccessAnyone(linkAccess) && { recipientEmailAddresses }),
  ...(getIsAccessAddSpecificRecipiens(linkAccess)
    ? {
        optionalNote,
        recipientEmailAddresses,
        anyoneWithNonPublicDomain,
        ...(anyoneWithNonPublicDomain && { emailDomain }),
      }
    : { linkDestination }),
});

export const validate = (values: CreateDataRoomShareableLinkForm) => {
  const {
    linkAccess,
    anyoneWithNonPublicDomain,
    recipientEmailAddresses,
    recipientEmailAddressesUnsubmited,
    includeNdaProtection,
    useExistingNda,
    dataRoomNda,
    dataRoomNdaId,
  } = values;

  const recipiensLentgh = getArrayOfEmails(
    recipientEmailAddresses,
    recipientEmailAddressesUnsubmited,
  )?.length;

  const areRecipiensRequired =
    getIsAccessAddSpecificRecipiens(linkAccess) &&
    !anyoneWithNonPublicDomain &&
    !recipiensLentgh;

  const isNdaUploadRequired = includeNdaProtection && !useExistingNda;
  const isExistingNdaRequired = includeNdaProtection && useExistingNda;

  return {
    ...(areRecipiensRequired && {
      recipientEmailAddresses: 'required',
    }),
    ...(isNdaUploadRequired && {
      dataRoomNda: validations.required()(dataRoomNda),
    }),
    ...(isExistingNdaRequired && {
      dataRoomNdaId: validations.required()(dataRoomNdaId),
    }),
  };
};

export const getNativeNdaDropzoneFile = (
  dataRoomNda: DataRoomNda,
  droppedFile: File,
): DropzoneFile => ({
  ...dataRoomNda.file,
  _id: dataRoomNda._id,
  name: droppedFile.name,
  type: dataRoomNda.file?.type || (FILE_TYPES.PDF as DropzoneFile['type']),
  url: dataRoomNda.file?.url || dataRoomNda.currentVersion?.pdfUrl,
});
